/**
 * AI Agents API Service
 *
 * Service functions for interacting with the AI agents API endpoints.
 */

import { apiClient } from "./api";
import type { Agent } from "@/types/security";

const API_PREFIX = "/api/v1/ai-agents";

/**
 * Backend response type (matches AIAgentStats schema)
 */
export interface AIAgentBackend {
  id: number;
  organization_id: number;
  name: string;
  description: string | null;
  trace_source: string;
  otel_scope_name: string | null;
  model_name: string;
  model_provider: string | null;
  critical_issues: number;
  high_issues: number;
  total_issues: number;
  trend_percentage: number;
  trend_direction: "up" | "down" | "flat";
  trace_count: number;
  first_seen_at: string;
  last_seen_at: string;
}

/**
 * Full agent detail (matches AIAgentRead schema)
 */
export interface AgentDetail {
  id: number;
  organization_id: number;
  trace_source: string;
  otel_scope_name: string | null;
  model_name: string;
  model_provider: string | null;
  agent_fingerprint: string;
  name: string;
  description: string | null;
  system_prompt_preview: string | null;
  raw_metadata: Record<string, unknown> | null;
  trace_count: number;
  issue_count: number;
  first_seen_at: string;
  last_seen_at: string;
  created_at: string;
  updated_at: string | null;
}

/**
 * Telemetry trace from backend
 */
export interface TelemetryTrace {
  id: number;
  timestamp: string;
  source: string;
  source_id: string;
  event_type: string;
  severity: string;
  anomaly_score: number;
  correlation_id: string | null;
  tags: string[];
  event_metadata: Record<string, unknown>;
  raw_data: Record<string, unknown>;
  created_at: string;
}

/**
 * Paginated response from backend
 */
export interface PaginatedResponse<T> {
  data: T[];
  total_count: number;
  has_more: boolean;
  page: number;
  items_per_page: number;
}

/**
 * Params for listing agent issues
 */
export interface AgentIssuesParams {
  page?: number;
  items_per_page?: number;
  severity?: string;
  status?: string;
  issue_type?: string;
  include_suppressed?: boolean;
}

/**
 * Params for listing agent traces
 */
export interface AgentTracesParams {
  page?: number;
  items_per_page?: number;
  severity?: string;
  event_type?: string;
}

/**
 * Backend security issue read (snake_case)
 */
export interface SecurityIssueBackend {
  id: number;
  organization_id: number;
  source_type: string | null;
  source_connection_id: number | null;
  agent_id: number | null;
  issue_type: string;
  failed_scanners: string[];
  severity: string;
  status: string;
  title: string;
  description: string;
  llm_guard_results: Record<string, unknown>;
  gemini_verification: Record<string, unknown> | null;
  risk_score: number;
  detected_at: string;
  verified_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string | null;
  is_deleted: boolean;
  deleted_at: string | null;
  trace_ids: number[];
  assigned_to_user_id: number | null;
  assigned_to_name: string | null;
  assigned_to_email: string | null;
  assigned_at: string | null;
  execution_traces: Record<string, unknown>[] | null;
  attack_flow: Record<string, unknown>[] | null;
  risks: Record<string, unknown> | null;
  suggested_fix: Record<string, unknown> | null;
}

/**
 * Transform backend response to frontend Agent type
 */
function transformAgent(data: AIAgentBackend): Agent {
  return {
    id: data.id,
    organizationId: data.organization_id,
    name: data.name,
    description: data.description,
    modelName: data.model_name,
    traceSource: data.trace_source,
    otelScopeName: data.otel_scope_name,
    modelProvider: data.model_provider,
    criticalIssues: data.critical_issues,
    highIssues: data.high_issues,
    totalIssues: data.total_issues,
    traceCount: data.trace_count,
    trendPercentage: data.trend_percentage,
    trendDirection: data.trend_direction,
    firstSeenAt: data.first_seen_at,
    lastSeenAt: data.last_seen_at,
  };
}

/**
 * List all AI agents with security issue statistics
 * @param days - Number of days for trend calculation (default: 30)
 */
export async function listAIAgents(days: number = 30, organizationId?: number): Promise<Agent[]> {
  const params = new URLSearchParams();
  params.append("days", days.toString());
  if (organizationId) params.append("organization_id", organizationId.toString());
  const url = `${API_PREFIX}?${params.toString()}`;
  const response = await apiClient.get<AIAgentBackend[]>(url);
  return response.map(transformAgent);
}

/**
 * Get a single AI agent by ID
 */
export async function getAgent(agentId: number): Promise<AgentDetail> {
  return apiClient.get<AgentDetail>(`${API_PREFIX}/${agentId}`);
}

/**
 * List security issues for a specific agent
 */
export async function listAgentIssues(
  agentId: number,
  params: AgentIssuesParams = {}
): Promise<PaginatedResponse<SecurityIssueBackend>> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.append("page", params.page.toString());
  if (params.items_per_page) searchParams.append("items_per_page", params.items_per_page.toString());
  if (params.severity) searchParams.append("severity", params.severity);
  if (params.status) searchParams.append("status", params.status);
  if (params.issue_type) searchParams.append("issue_type", params.issue_type);
  if (params.include_suppressed) searchParams.append("include_suppressed", "true");

  const qs = searchParams.toString();
  const url = `${API_PREFIX}/${agentId}/issues${qs ? `?${qs}` : ""}`;
  return apiClient.get<PaginatedResponse<SecurityIssueBackend>>(url);
}

/**
 * List telemetry traces for a specific agent
 */
export async function listAgentTraces(
  agentId: number,
  params: AgentTracesParams = {}
): Promise<PaginatedResponse<TelemetryTrace>> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.append("page", params.page.toString());
  if (params.items_per_page) searchParams.append("items_per_page", params.items_per_page.toString());
  if (params.severity) searchParams.append("severity", params.severity);
  if (params.event_type) searchParams.append("event_type", params.event_type);

  const qs = searchParams.toString();
  const url = `${API_PREFIX}/${agentId}/traces${qs ? `?${qs}` : ""}`;
  return apiClient.get<PaginatedResponse<TelemetryTrace>>(url);
}

// ---------------------------------------------------------------------------
// Agent Detail Stats (#95)
// ---------------------------------------------------------------------------

export type StatsTimeWindow = "7" | "30" | "90";

export interface AgentDetailStats {
  days: number;
  period_start: string;
  period_end: string;
  severity_counts: { critical: number; high: number; medium: number; low: number; info: number };
  severity_trends: Record<string, number>;
  status_counts: {
    open: number;
    in_review: number;
    resolved: number;
    false_positive: number;
    suppressed: number;
  };
  total_issues: number;
  total_issues_trend_percentage: number;
  total_issues_trend_direction: "up" | "down" | "flat";
  avg_risk_score: number;
  top_issue_types: Array<{ name: string; count: number }>;
  traces: {
    total: number;
    trend_percentage: number;
    trend_direction: "up" | "down" | "flat";
    by_event_type: Array<{ name: string; count: number }>;
    avg_anomaly_score: number;
  };
  daily_issues: Array<{ date: string; count: number }>;
}

export async function getAgentStats(agentId: number, days: number = 30): Promise<AgentDetailStats> {
  return apiClient.get<AgentDetailStats>(`${API_PREFIX}/${agentId}/stats?days=${days}`);
}

// ---------------------------------------------------------------------------
// Agent Metrics (#92)
// ---------------------------------------------------------------------------

export type MetricsWindow = "1h" | "6h" | "24h" | "7d" | "30d" | "90d";

export interface MetricsDataPoint {
  bucket: string;
  request_count: number;
  avg_latency_ms: number;
  p95_latency_ms: number | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  estimated_cost: number;
  error_count: number;
}

export interface MetricsSummary {
  total_requests: number;
  avg_latency_ms: number;
  p95_latency_ms: number | null;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  estimated_cost: number;
  error_rate: number;
  tokens_per_request: number;
}

export interface AgentMetricsResponse {
  agent_id: number;
  window_start: string;
  window_end: string;
  granularity: string;
  summary: MetricsSummary;
  time_series: MetricsDataPoint[];
}

export async function getAgentMetrics(
  agentId: number,
  window: MetricsWindow = "7d",
  granularity: string = "auto"
): Promise<AgentMetricsResponse> {
  const params = new URLSearchParams({ window, granularity });
  return apiClient.get<AgentMetricsResponse>(
    `${API_PREFIX}/${agentId}/metrics?${params.toString()}`
  );
}

/**
 * Update an AI agent's name and/or description
 */
export async function updateAgent(
  agentId: number,
  data: { name?: string; description?: string }
): Promise<AgentDetail> {
  return apiClient.patch<AgentDetail>(`${API_PREFIX}/${agentId}`, data);
}
